// Leniwe sekcje pod produktem (15 §3A–B): <product-recommendations> i <recently-viewed>.
import { Theme, fetchSection } from '@theme/core';

const HISTORY_KEY = 'viewedProducts'; // kontrakt F1: JSON tablica ID (stringi), najnowszy pierwszy, maks. 12

// FR-15-05: odznaka Judge.me w karcie (ręczny znacznik, nie app block) przychodzi ukryta; skrypt aplikacji
// odsłania tylko węzły widziane przy starcie albo po customizeBadges().
const revealBadges = () => window.jdgm?.customizeBadges?.();

// Raz, gdy sekcja zbliży się na 400 px do dołu okna (FR-15-02, FR-15-13).
class LazySection extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains('is-loaded')) return;
    this.controller = new AbortController();
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        this.observer.disconnect();
        this.load(this.controller.signal).catch((error) => {
          if (error.name !== 'AbortError') this.fail(error); // przerwanie = odłączenie, nie błąd
        });
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
  }
}

class ProductRecommendations extends LazySection {
  async load(signal) {
    const { url, productId, intent, limit } = this.dataset;
    if (!productId) return;
    // Render po nazwie pliku (§5.5) — działa też dla niezapisanej instancji w edytorze.
    const doc = await fetchSection('product-recommendations', { url, params: { product_id: productId, intent, limit }, signal });
    const list = doc.querySelector('[data-recommendations-list]');
    if (!list) throw new Error('brak listy w odpowiedzi');
    // Zastępstwo z katalogu może zawierać oglądany produkt — usuwamy go i przycinamy do limitu.
    let count = 0;
    for (const item of [...list.children]) {
      if (item.dataset.productId === productId || ++count > Number(limit)) item.remove();
    }
    if (!list.children.length) {
      this.hidden = true; // FR-15-06: bez produktów znika cała sekcja
      return;
    }
    this.querySelector('[data-recommendations-list]').replaceWith(list);
    this.classList.add('is-loaded');
    revealBadges();
  }

  fail(error) {
    console.error('Produkty powiązane:', error);
    // W edytorze szkielet zostaje, żeby sekcję dało się zobaczyć i zaznaczyć.
    if (!Theme.designMode) this.hidden = true;
  }
}

function history() {
  try {
    const ids = JSON.parse(localStorage.getItem(HISTORY_KEY));
    return Array.isArray(ids) ? ids.map(String).filter((id) => /^\d+$/.test(id)) : [];
  } catch {
    return [];
  }
}

class RecentlyViewed extends LazySection {
  async load(signal) {
    const { url, productId, limit } = this.dataset;
    const ids = history().filter((id) => id !== productId);
    if (!ids.length) return;
    const target = new URL(url, location.href);
    target.search = new URLSearchParams({ q: ids.map((id) => `id:${id}`).join(' OR '), type: 'product', section_id: 'product-cards' });
    // Zwykły fetch, nie fetchSection: ten przerywa poprzednie żądanie tej samej sekcji, a product-cards
    // pobierają w tym samym czasie także zestawy (G1).
    const response = await fetch(target, { signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const list = doc.querySelector('[data-product-cards]');
    if (!list) return;
    // Kolejność historii, nie wyszukiwarki; produktów spoza wyników (wycofane, ukryte) po prostu brak (FR-15-15).
    const items = [...list.children];
    const byId = new Map(items.map((item) => [item.dataset.productId, item])); // kontrakt product-cards: li[data-product-id]
    const ordered = ids.map((id) => byId.get(id)).filter(Boolean);
    const cards = (ordered.length ? ordered : items).slice(0, Number(limit));
    if (!cards.length) return;
    list.replaceChildren(...cards);
    this.append(list);
    this.classList.add('is-loaded');
    revealBadges();
  }

  fail(error) {
    console.error('Ostatnio oglądane:', error); // sekcja zostaje zwinięta
  }
}

if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);
